'use client';

import { RefObject, useCallback, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { ImageGalleryProps, ImageGalleryRef } from 'react-image-gallery';
import { X } from 'lucide-react';
import { LazyImage } from '../LazyImage';

// Dynamically import react-image-gallery to avoid SSR issues
const ReactImageGallery = dynamic<ImageGalleryProps & { forwardedRef: RefObject<ImageGalleryRef> }>(
  async () => {
    const Component = (await import('react-image-gallery')).default;
    return ({ forwardedRef, ...props }: any) => <Component ref={forwardedRef} {...props} />;
  },
  {
    ssr: false,
    loading: () => <div className="text-center py-8">Loading gallery...</div>,
  }
);

export interface ImageGalleryItem {
  original: string;
  thumbnail?: string;
  description?: string;
}

export type ImageGalleryDisplayMode = 'gallery' | 'grid';

interface ImageGalleryComponentProps {
  items: ImageGalleryItem[];
  displayMode?: ImageGalleryDisplayMode;
  showPlayButton?: boolean;
  showFullscreenButton?: boolean;
  showNav?: boolean;
  autoPlay?: boolean;
  slideDuration?: number;
  slideInterval?: number;
  className?: string;
}

export function ImageGallery({
  items,
  displayMode = 'gallery',
  showPlayButton = false,
  showFullscreenButton = false,
  showNav = true,
  autoPlay = false,
  slideDuration = 450,
  slideInterval = 3000,
  className = '',
}: ImageGalleryComponentProps) {
  const galleryRef = useRef<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const lightboxRef = useRef<any>(null);

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  if (items.length === 0) {
    return null;
  }

  if (displayMode === 'grid') {
    return (
      <div className={className}>
        {/* Grid layout */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((item, index) => (
            <button
              key={item.original}
              onClick={() => openLightbox(index)}
              className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <LazyImage
                src={item.original}
                alt={item.description || `Gallery image ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
          ))}
        </div>

        {/* Fullscreen lightbox */}
        {lightboxOpen && (
          <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center">
            <button
              onClick={closeLightbox}
              className="fixed top-4 right-4 z-[110] bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors cursor-pointer"
              aria-label="Close gallery"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-full max-w-5xl mx-auto flex items-center overflow-hidden image-gallery-lightbox p-2">
              <div className="w-full min-w-0">
                <ReactImageGallery
                  forwardedRef={lightboxRef}
                  items={items}
                  startIndex={lightboxIndex}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showNav={true}
                  showThumbnails={true}
                  slideDuration={slideDuration}
                  useBrowserFullscreen={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default: gallery mode (react-image-gallery inline)
  return (
    <div className={className}>
      {isFullscreen && (
        <button
          onClick={() => galleryRef.current?.exitFullScreen()}
          className="fixed top-4 right-4 z-[110] bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors cursor-pointer"
          aria-label="Close fullscreen"
        >
          <X className="w-6 h-6" />
        </button>
      )}
      <ReactImageGallery
        forwardedRef={galleryRef}
        items={items}
        showPlayButton={showPlayButton}
        showFullscreenButton={showFullscreenButton}
        showNav={showNav}
        autoPlay={autoPlay}
        slideDuration={slideDuration}
        useBrowserFullscreen={false}
        slideInterval={slideInterval}
        showThumbnails={isFullscreen}
        onScreenChange={(fullscreen: boolean) => setIsFullscreen(fullscreen)}
        onClick={() => {
          if (galleryRef.current && !isFullscreen) {
            galleryRef.current.toggleFullScreen();
          }
        }}
      />
    </div>
  );
}
